import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { startTrialCardCapture, TrialCardQuote } from '@extension/wa-communication-layer/api/api-calls';
import { nextStepAfterStart, openPendingTab, trialCardReturnUrl } from '@common/components/trial-upgrade-prompt/trial-upgrade-prompt-logic';

interface Props {
  /** What the screen shows; the same figures are sent back on submit so the server can refuse a moved price. */
  quote: TrialCardQuote;
  /** The popup's server-supplied "Not now" label, so no English literal lives here. */
  notNowLabel: string;
  onCancel: () => void;
}

/**
 * #1229: the card-add consent screen. All wording comes from the server (the same text the consent row
 * records). The checkbox starts unticked and the button stays disabled until it is ticked. "Not now" has
 * the same size and style as the primary button. Submitting records the consent and sends the customer to
 * the Chargebee hosted page; they come back to `?trialCard=return` (see TrialCardReturn).
 */
export const TrialCardConsent: React.FC<Props> = ({ quote: initialQuote, notNowLabel, onCancel }) => {
  const { t } = useTranslation(namespace);
  const [failed, setFailed] = useState(false);
  const [quote, setQuote] = useState<TrialCardQuote>(initialQuote);
  const copy = quote.copy;
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const equalButtonClass = 'min-w-[9rem] flex-1 h-auto whitespace-normal py-2';

  const submit = (): void => {
    if (!accepted || busy) return;
    setBusy(true);
    setFailed(false);
    // Open the hosted-page tab NOW, inside the click: a tab opened after the async call is popup-blocked.
    const tab = openPendingTab();
    startTrialCardCapture({
      checkboxAccepted: true,
      locale: navigator.language,
      country: '',
      redirectUrl: trialCardReturnUrl(),
      shown: { itemPriceId: quote.itemPriceId, monthlyMinor: quote.monthlyMinor, currency: quote.currency, chargeDateEpoch: quote.chargeDateEpoch },
    })
      .then((res) => {
        const step = nextStepAfterStart(res);
        if (step.kind === 'navigate') {
          tab.navigate(step.url);
        } else if (step.kind === 'reshow') {
          // Chargebee moved since the screen: show the fresh figures and make the customer tick again.
          tab.close();
          setQuote(step.quote);
          setAccepted(false);
          setBusy(false);
        } else {
          // Never the charging checkout from a "nothing charged" screen: stay here and let the customer retry.
          tab.close();
          setFailed(true);
          setBusy(false);
        }
      })
      .catch(() => { tab.close(); setFailed(true); setBusy(false); });
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent className="max-w-md" data-testid="trial-card-consent">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.body}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{copy.cancelLine} {copy.refundLine} {copy.emailNotice}</p>
        {copy.euWithdrawal !== null && <p className="text-sm text-muted-foreground">{copy.euWithdrawal}</p>}
        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(v === true)} data-testid="trial-card-consent-checkbox" />
          <span>{copy.checkbox}</span>
        </label>
        <p className="text-xs text-muted-foreground">{copy.partnerNotice}</p>
        {failed && <p role="alert" className="text-sm text-destructive" data-testid="trial-card-consent-error">{t('common.trialCardStartFailed')}</p>}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className={equalButtonClass} onClick={onCancel}>{notNowLabel}</Button>
          <Button variant="outline" className={equalButtonClass} disabled={!accepted || busy} onClick={submit}>{copy.button}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
